import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { HierarchyView } from 'sharedComponents/HierarchyView';
import { renderIcon, createPowertrainNodes, createDropHandlerWithCleanup, canDrop, createAssetNodes } from './helpers';
import { NODE_TYPE } from './constants';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import HierarchyExternalNodes from './HierarchyExternalNodes';

const HierarchyLeftContent = (props) => {
    const {
        transformedHierarchyData,
        nodeMap,
        selectedIds,
        expandedIds,
        setSelectedIds,
        setExpandedIds,
        handleDrop,
        isEditHierarchy,
        renderActions,
        handleSaveNewNode,
        handleCancelNewNode,
        filteredPowertrains,
        setSelectedPowertrain,
        setSearchTerm,
        updateHierarchyLoadingStatus,
        setPartialPowertrainFailure,
        setHierarchyErrorModal,
        setSaveFailed,
        isSearchActive
    } = props;

    const [searchboxFilter, setSearchboxFilter] = useState('');
    const [selectedPowertrains, setSelectedPowertrains] = useState([]);
    const [selectedAssets, setSelectedAssets] = useState([]);
    const [assetListForSelectedNode, setAssetListForSelectedNode] = useState([]);
    const [powertrainListForSelectedNode, setPowertrainListForSelectedNode] = useState([]);

    // Filter powertrains based on search
    const searchFilteredPowertrains = useMemo(() => {
        const siteData = nodeMap[nodeMap[selectedIds[0]]?.data?.siteId];
        if (!siteData) {
            setPowertrainListForSelectedNode([]);
            return [];
        }

        // Find the unassigned powertrains node in the site's children
        const unassignedPowertrainsNode = siteData.children?.find(child =>
            child.data?.type === NODE_TYPE.UNASSIGNED_POWERTRAINS
        );

        if (!unassignedPowertrainsNode || !unassignedPowertrainsNode.children) {
            setPowertrainListForSelectedNode([]);
            return [];
        }

        // Get the children data (the actual powertrain objects)
        const sitePowertrains = unassignedPowertrainsNode.children.map(child => child.data);
        setPowertrainListForSelectedNode(sitePowertrains);

        if (!searchboxFilter) {
            return sitePowertrains;
        }

        const filterValue = searchboxFilter.toLowerCase();
        return sitePowertrains.filter(powertrain => {
            if (powertrain.name?.toLowerCase().includes(filterValue)) {
                return true;
            }
            return (powertrain.assets || []).some(asset =>
                String(asset.name || '').toLowerCase().includes(filterValue) ||
                String(asset.id || '').toLowerCase().includes(filterValue)
            );
        });
    }, [selectedIds, nodeMap, searchboxFilter]);

    // Filter assets based on search box input
    const filteredAssets = (assets) => {
        return assets?.filter(asset => {
            if (!searchboxFilter.trim()) {
                return true;
            }

            const filterValue = searchboxFilter.toLowerCase();
            const assetName = String(asset.name || '').toLowerCase();
            const assetId = String(asset.id || '').toLowerCase();

            return assetName.includes(filterValue) ||
                assetId.includes(filterValue);
        }) || [];
    };

    const searchFilteredAssets = useMemo(() => {
        const selectedNode = nodeMap[selectedIds?.[0]];
        let assets = [];
        if ([NODE_TYPE.SITE, NODE_TYPE.LOCATION].includes(selectedNode?.type)) {
            assets = selectedNode.children?.find(child => child.data?.type === NODE_TYPE.UNASSIGNED_ASSETS)?.data?.assets || [];
        }
        setAssetListForSelectedNode(assets);
        return filteredAssets(assets);
    }, [selectedIds, nodeMap, searchboxFilter]);

    const assetNodes = useMemo(() => {
        const siteId = nodeMap[selectedIds?.[0]]?.data?.siteId;
        const parentId = nodeMap[selectedIds?.[0]]?.children?.find(child => child.data?.type === NODE_TYPE.UNASSIGNED_ASSETS)?.id;
        return createAssetNodes(searchFilteredAssets, siteId, parentId);
    }, [searchFilteredAssets]);

    // Transform powertrains into node format for HierarchyNode
    const powertrainNodes = useMemo(() => {
        return createPowertrainNodes(searchFilteredPowertrains);
    }, [searchFilteredPowertrains]);

    // Full (unfiltered) node lists so items selected before a search filter is
    // applied can still be resolved and dragged.
    const allPowertrainNodes = useMemo(() => {
        return createPowertrainNodes(powertrainListForSelectedNode || []);
    }, [powertrainListForSelectedNode]);

    const allAssetNodes = useMemo(() => {
        const siteId = nodeMap[selectedIds?.[0]]?.data?.siteId;
        const parentId = nodeMap[selectedIds?.[0]]?.children?.find(child => child.data?.type === NODE_TYPE.UNASSIGNED_ASSETS)?.id;
        return createAssetNodes(assetListForSelectedNode || [], siteId, parentId);
    }, [assetListForSelectedNode, selectedIds, nodeMap]);

    // Enhanced drop handler that removes external nodes after successful drop
    const handleDropWithCleanup = useMemo(() => {
        return createDropHandlerWithCleanup(handleDrop, setSelectedPowertrains, setSelectedAssets);
    }, [handleDrop, setSelectedPowertrains, setSelectedAssets]);

    const handleSelectNode = useCallback((selectedIds) => {
        setSelectedIds(selectedIds);
        setSearchTerm(null);
        setSelectedPowertrains([]); // Clear selected powertrains when selecting a node
        setSelectedAssets([]); // Clear selected assets when selecting a node
        // If selected node is a powertrain, update selectedPowertrain
        if ([NODE_TYPE.POWERTRAIN, NODE_TYPE.UNASSIGNED_POWERTRAIN].includes(nodeMap[selectedIds[0]]?.data?.type)) {
            const selectedNodeData = nodeMap[selectedIds[0]]?.data;
            // Try to find in filteredPowertrains first (for unassigned powertrains)
            const powertrainData = filteredPowertrains?.find(pt => pt.powertrainId === selectedNodeData?.powertrainId);
            setSelectedPowertrain(powertrainData || null);
        } else {
            setSelectedPowertrain(null);
        }
    }, [setSelectedIds, setSearchTerm, setSelectedPowertrain, filteredPowertrains, nodeMap]);

    const isShowExternalNodes = isEditHierarchy && selectedIds?.length > 0 &&
        [NODE_TYPE.SITE, NODE_TYPE.LOCATION].includes(nodeMap[selectedIds[0]]?.data?.type);

    return (
        <LoadingStatusWrapper
            loadingStatus={[updateHierarchyLoadingStatus]}
            transparent
            useCustomAnchor
        >
            <div className='hierarchy-left-content'>
                <HierarchyView
                    nodes={transformedHierarchyData}
                    nodeMap={nodeMap}
                    isflattenData={false}
                    selectedIds={selectedIds}
                    expandedIds={expandedIds}
                    renderIcon={renderIcon}
                    onSelect={handleSelectNode}
                    onExpand={(node) => {
                        setExpandedIds([...expandedIds, node.id]);
                    }}
                    onCollapse={(node) => {
                        setExpandedIds(expandedIds.filter(id => id !== node.id));
                    }}
                    onDrop={handleDropWithCleanup}
                    canDrop={(nodeMap, draggedNodes, targetNode, dropPosition) => {
                        return canDrop(
                            nodeMap,
                            draggedNodes,
                            targetNode,
                            dropPosition,
                            setPartialPowertrainFailure,
                            setHierarchyErrorModal,
                            setSelectedPowertrains,
                            setSaveFailed
                        );
                    }}
                    hiddenNodeTypes={[
                        NODE_TYPE.UNASSIGNED_POWERTRAINS,
                        NODE_TYPE.UNASSIGNED_POWERTRAIN,
                        NODE_TYPE.UNASSIGNED_ASSETS,
                        NODE_TYPE.ASSET
                    ]}
                    dragEnabled={isEditHierarchy}
                    dropEnabled={isEditHierarchy}
                    searchEnabled={false}
                    height={window.innerHeight - 300}
                    showExpandIcon={!isSearchActive}
                    renderActions={renderActions}
                    editMode={isEditHierarchy}
                    onSaveNewNode={handleSaveNewNode}
                    onCancelNewNode={handleCancelNewNode}
                    externalNodeItems={[...powertrainNodes, ...assetNodes]}
                    allExternalNodeItems={[...allPowertrainNodes, ...allAssetNodes]}
                    isShowExternalNodes={isShowExternalNodes}
                    externalNode={<HierarchyExternalNodes
                        nodeMap={nodeMap}
                        selectedIds={selectedIds}
                        powertrainNodes={powertrainNodes}
                        renderActions={renderActions}
                        dragEnabled={isEditHierarchy}
                        itemHeight={32}
                        selectedPowertrains={selectedPowertrains}
                        setSelectedPowertrains={setSelectedPowertrains}
                        searchboxFilter={searchboxFilter}
                        setSearchboxFilter={setSearchboxFilter}
                        assetNodes={assetNodes}
                        setSelectedAssets={setSelectedAssets}
                        selectedAssets={selectedAssets}
                        assetListForSelectedNode={assetListForSelectedNode}
                        powertrainListForSelectedNode={powertrainListForSelectedNode}
                    />}
                    selectedExternalIds={[...selectedPowertrains, ...selectedAssets]}
                />
            </div>
        </LoadingStatusWrapper>
    );
};

HierarchyLeftContent.propTypes = {
    transformedHierarchyData: PropTypes.array,
    nodeMap: PropTypes.object,
    selectedIds: PropTypes.array,
    expandedIds: PropTypes.array,
    setSelectedIds: PropTypes.func,
    setExpandedIds: PropTypes.func,
    handleDrop: PropTypes.func,
    isEditHierarchy: PropTypes.bool,
    renderActions: PropTypes.func,
    handleSaveNewNode: PropTypes.func,
    handleCancelNewNode: PropTypes.func,
    showPowertrains: PropTypes.bool,
    filteredPowertrains: PropTypes.array,
    setSelectedPowertrain: PropTypes.func,
    setSearchTerm: PropTypes.func,
    updateHierarchyLoadingStatus: PropTypes.object,
    setPartialPowertrainFailure: PropTypes.func,
    setHierarchyErrorModal: PropTypes.func,
    setSaveFailed: PropTypes.func,
    onRemovePowertrainsFromList: PropTypes.func,
    isSearchActive: PropTypes.bool
};

export default HierarchyLeftContent;
